<template>
  <div
    class="app-field"
    :class="[
      attrs.class,
      {
        'app-field-invalid': failed,
        'app-field-readonly': readonly,
        'app-field-entry': size === 'entry',
        'app-field-decimal': kind === 'decimal',
      },
    ]"
    :style="attrs.style"
  >
    <div class="label" :class="{ unseen: hideLabel, question }">
      <label :for="id">{{ label }}</label>
      <slot name="label-extra" />
    </div>

    <div class="well">
      <span v-if="$slots.lead" class="lead" aria-hidden="true"><slot name="lead" /></span>
      <textarea
        v-if="kind === 'multiline'"
        :id="id"
        class="control"
        v-bind="control()"
        :value="modelValue"
        :readonly="readonly"
        :placeholder="placeholder"
        :aria-invalid="failed ? 'true' : undefined"
        :aria-errormessage="failed ? `${id}-error` : undefined"
        :aria-describedby="describedBy()"
        @input="update"
      ></textarea>
      <select
        v-else-if="kind === 'select'"
        :id="id"
        class="control select"
        :class="{ placeholder: !modelValue }"
        v-bind="control()"
        :value="modelValue"
        :disabled="readonly || !!attrs.disabled"
        :aria-invalid="failed ? 'true' : undefined"
        :aria-errormessage="failed ? `${id}-error` : undefined"
        :aria-describedby="describedBy()"
        @change="update"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option v-for="option in options" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <!-- The chosen option as words that wrap, drawn only with large text over the select (MOL-321). -->
      <span
        v-if="kind === 'select'"
        class="chosen-words"
        :class="{ placeholder: !modelValue }"
        aria-hidden="true"
        >{{ options.find((option) => option.value === modelValue)?.label ?? placeholder }}</span
      >
      <IconChevronDown v-if="kind === 'select'" class="chevron" aria-hidden="true" />
      <span v-if="shows" class="shown" aria-hidden="true">
        <IconCalendar class="shown-icon" />{{ display }}
      </span>
      <!-- The box a figure typed shrinks to beside what else the well holds (MOL-321); no box of its own but for an
           amount of the body's size. -->
      <span v-if="kind !== 'multiline' && kind !== 'select'" class="control-box">
        <input
          :id="id"
          class="control"
          :class="{ veiled: shows }"
          v-bind="control()"
          :style="
            size === 'entry'
              ? entryWidth('figure')
              : kind === 'decimal'
                ? figureChars(modelValue || placeholder || '0', 'text')
                : undefined
          "
          :type="kind === 'date' ? 'date' : 'text'"
          :inputmode="kind === 'decimal' ? 'decimal' : kind === 'digits' ? 'numeric' : undefined"
          :value="modelValue"
          :readonly="readonly"
          :placeholder="placeholder"
          :aria-invalid="failed ? 'true' : undefined"
          :aria-errormessage="failed ? `${id}-error` : undefined"
          :aria-describedby="describedBy()"
          @input="update"
        />
      </span>
      <span
        v-if="$slots.suffix"
        :id="`${id}-suffix`"
        class="suffix"
        :style="size === 'entry' ? entryWidth('sign') : undefined"
      >
        <slot name="suffix" />
      </span>
    </div>

    <AppReveal>
      <p v-if="failed" :id="`${id}-error`" class="error">
        <IconAlert class="alert" aria-hidden="true" />{{ errorText ?? t(error ?? '') }}
      </p>
    </AppReveal>
    <!-- Read with the field when it is focused, not at every letter: two hundred announcements over
         the echo of the typing drown it (MOL-147, review №4). -->
    <p v-if="counterFrom !== null" :id="`${id}-counter`" class="counter">{{ counter }}</p>
    <!-- Said only at its marks, through a region there from the start, its words alone changing. -->
    <p v-if="counterFrom !== null" class="counter-spoken" aria-live="polite">{{ said }}</p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onUnmounted, ref, useAttrs, useId, useSlots, watch } from 'vue'
import type { PropType, StyleValue } from 'vue'
import { useI18n } from 'vue-i18n'
import IconAlert from '~icons/mdi/alert-circle-outline'
import IconCalendar from '~icons/mdi/calendar-blank-outline'
import IconChevronDown from '~icons/mdi/chevron-down'
import type { ErrorCode } from '@molvia/model'
import AppReveal from '@/components/AppReveal.vue'
import { figureChars, figureWidth, widthStyle } from '@/components/figure'
import { LINGER_MS } from '@/composables/useAnnouncer'

/** The sign of `entry` beside its figure, a share of the figure's size: 32 to 40 (MOL-235). */
const ENTRY_SIGN = 0.8

/** How many characters left are said aloud, beside the moment the count comes and the end. */
const SPOKEN_MARKS = [20, 100]

export type FieldKind = 'text' | 'decimal' | 'digits' | 'multiline' | 'date' | 'select'

/** `entry` is the amount a sheet is opened to type (MOL-235): Entry 40 in a well of 72, the sign 32. */
export type FieldSize = 'body' | 'entry'

/**
 * A native input with its label, its error and its tail, wired the same way every time: the
 * label by `for`, the error by `aria-describedby` and `aria-invalid`, the tail (the dram sign)
 * read out after the value rather than typed into it.
 *
 * The value is the raw string. Nothing here parses or rounds — `1,128` comes out as `1,128`,
 * and the screen reads it through the model's codecs, because the server is the one that
 * decides what a number is.
 *
 * `kind` sets what a phone needs to open the right keyboard, so a screen never has to remember
 * it. Money and quantities are `type="text" inputmode="decimal"`, never `type="number"`: that
 * one gives spinners, refuses the comma of a Russian keyboard and silently drops what it cannot
 * read. `digits` is a code copied from a package — the barcode's digits (MOL-98): the digit pad,
 * and still text, so a leading zero stays.
 *
 * The error is a code from the domain registry and doubles as its i18n key, so no message is
 * written where it is shown; an 18 alert stands before it (DESIGN.md, owner's В-3 «а» of MOL-235), so
 * an error is told by more than its colour in bad light. `errorText` is for a refusal the registry has no code for — the
 * screen's own words, already translated: the review's «a character that cannot be saved»
 * (MOL-28, G2). It wins over `error` when both are given.
 *
 * Every other attribute — `autofocus`, `enterkeyhint`, `maxlength` — goes to the control
 * itself; `class` and `style` stay on the field, where the layout is. The field's own classes are
 * the kit's words (`app-field…`, its states too): a root carries the scope of the screen it stands in,
 * and a sheet's `.field` — its column of a label and a field — doubled the space under the label
 * (MOL-235).
 *
 * **`size="entry"` is the amount a sheet is opened to type** (MOL-235, Ф-12): the spending, the
 * check, the transfer had each drawn its own copy of it, with three looks of the focus. The same
 * field, its label, its error and its tail the same — only drawn large, the sign in the tail.
 */
export default defineComponent({
  name: 'AppField',
  components: { AppReveal, IconAlert, IconCalendar, IconChevronDown },
  inheritAttrs: false,
  props: {
    modelValue: { type: String, required: true },
    label: { type: String, required: true },
    kind: { type: String as PropType<FieldKind>, default: 'text' },
    size: { type: String as PropType<FieldSize>, default: 'body' },
    error: { type: String as PropType<ErrorCode | null>, default: null },
    errorText: { type: String as PropType<string | null>, default: null },
    options: {
      type: Array as PropType<readonly { value: string; label: string }[]>,
      default: () => [],
    },
    placeholder: { type: String, default: undefined },
    readonly: { type: Boolean, default: false },
    /**
     * A date's value in words — «Сегодня, 26 сентября» — drawn over the native field, which stays
     * underneath to open the system picker and to be read aloud by its own value (MOL-82, handoff 07).
     * Only for `kind="date"`, and only while it has words: an empty one shows the field as it is.
     */
    display: { type: String as PropType<string | null>, default: null },
    /**
     * Where the card's own caption already names the field — «Категория во времени» over its select
     * (MOL-74): the label is still there for a screen reader, only not drawn.
     */
    hideLabel: { type: Boolean, default: false },
    /**
     * The label is the sheet's one question — «Сколько на счёте сейчас?» of the check (handoff 116 v2,
     * owner's В-2 «а» of MOL-235): 17/600 in the text's colour, not the 13 of a field among others.
     */
    question: { type: Boolean, default: false },
    /**
     * How many characters left of `maxlength` start to be counted under the field (MOL-147): silent
     * before, so a long message is not met by a number from its first letter, and not cut short at
     * the bound in silence either. Counted as the field and the schema count — UTF-16 units.
     */
    counterFrom: { type: Number as PropType<number | null>, default: null },
  },
  emits: {
    'update:modelValue': (value: string) => typeof value === 'string',
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const attrs = useAttrs()
    const slots = useSlots()
    const id = useId()
    const failed = computed(() => props.errorText !== null || props.error !== null)

    // Functions, not computeds: neither attrs nor slots are reactive, and a computed would keep
    // what it saw first — an `enterkeyhint` added later, a tail that came with the unit.
    function control(): Record<string, unknown> {
      return Object.fromEntries(
        Object.entries(attrs).filter(([name]) => name !== 'class' && name !== 'style'),
      )
    }

    // The tail first, right after the value, then the error, then what the screen adds: a hint of
    // twenty words between the figure and its «֏» left the sign alone at the end (MOL-235, А4).
    function describedBy(): string | undefined {
      const parts = [
        slots.suffix ? `${id}-suffix` : undefined,
        failed.value ? `${id}-error` : undefined,
        attrs['aria-describedby'],
        counter.value ? `${id}-counter` : undefined,
      ].filter(Boolean)
      return parts.length > 0 ? parts.join(' ') : undefined
    }

    /**
     * The figure typed and its sign shrink together to the field (MOL-321, adversarial З1, И1): their width in the
     * figure's size, the sign at its share — `ENTRY_SIGN`, or the figure's own for a code of letters, «RSD», which
     * at that share was 12 px beside figures of 15 at 200 %. At 200 % «125403,50» beside «₽» of its own size was
     * 12.9 px. The sign is read from the tail's text.
     */
    function entryWidth(part: 'figure' | 'sign'): StyleValue {
      const sign = (slots.suffix?.() ?? [])
        .map((node) => (typeof node.children === 'string' ? node.children : ''))
        .join('')
      const share = sign.trim().length > 1 ? 1 : ENTRY_SIGN
      const figure = figureWidth(props.modelValue || (props.placeholder ?? '') || '0')
      const width = figure + share * figureWidth(sign)
      return widthStyle(part === 'figure' ? width : width / share)
    }

    function update(event: Event): void {
      emit(
        'update:modelValue',
        (event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value,
      )
    }

    const shows = computed(() => props.kind === 'date' && !!props.display)

    // `maxlength` is read from `attrs`, which are not reactive: it does not change, the value does.
    const max = computed(() => Number(attrs.maxlength))
    /** How many are left while the count shows, `null` while it is silent. */
    const left = computed(() => {
      if (props.counterFrom === null || !Number.isInteger(max.value)) return null
      const rest = Math.max(max.value - props.modelValue.length, 0)
      return rest > props.counterFrom ? null : rest
    })
    const words = (n: number): string =>
      n === 0 ? t('field.full', { max: max.value }) : t('field.left', { n }, n)
    const counter = computed(() => (left.value === null ? '' : words(left.value)))

    // Said aloud only as a mark is passed on the way down — the count coming, 100, 20, the end — and
    // then what is truly left, a paste past two marks included; going up is silent. The words go
    // after a while, as the app's region lets its own: left there, browse mode reads them as still
    // true (review №8, adversarial Н3).
    const marks = computed(() =>
      [0, ...SPOKEN_MARKS, props.counterFrom ?? 0]
        .filter((mark) => mark <= (props.counterFrom ?? 0))
        .sort((a, b) => a - b),
    )
    const markOf = (rest: number | null): number =>
      rest === null ? Infinity : (marks.value.find((mark) => rest <= mark) ?? Infinity)
    const said = ref('')
    let lingering: ReturnType<typeof setTimeout> | undefined
    watch(left, (now, before) => {
      if (now === null || markOf(now) >= markOf(before)) return
      said.value = words(now)
      clearTimeout(lingering)
      lingering = setTimeout(() => {
        said.value = ''
      }, LINGER_MS)
    })
    onUnmounted(() => {
      clearTimeout(lingering)
    })

    return {
      t,
      attrs,
      id,
      failed,
      shows,
      counter,
      said,
      control,
      describedBy,
      update,
      entryWidth,
      figureChars,
    }
  },
})
</script>

<style scoped lang="scss">
/* No least width of its own either (MOL-318): WebKit keeps a select's longest option as its least width
   whatever the width of the select, and a field in a grid of a column `auto` widened the column. */
.app-field {
  min-width: 0;
}

/* What stands beside the label goes under it where the two do not fit (MOL-321): «· необязательно» stood past the
   sheet of a spending's day and note at 200 %. */
.label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
  color: var(--text-muted);
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
}

.unseen {
  @include visually-hidden;
}

.question {
  color: var(--text);
  font-size: var(--text-headline);
}

/* A mark before the value — the colour of the category chosen (MOL-74); the words are the value's. */
.lead {
  display: flex;
  flex: none;
}

.well {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--touch-target);
  padding: 0 var(--space-3);
  border: var(--hairline) solid var(--border-strong);
  border-radius: var(--radius);
  background: var(--surface-2);
  transition:
    border-color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out),
    outline-color var(--dur-fast) var(--ease-out);

  &:focus-within {
    @include field-focus;
  }
}

/* Not a box of the well's row: the control stands in it as before — an amount of the body's size gives it one. */
.control-box {
  display: contents;
}

.app-field-decimal:not(.app-field-entry) {
  .control-box {
    container-type: inline-size;
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
  }

  /* An amount shrinks to what the well leaves it (MOL-321): at 200 % «125403,50» was 192 px in 165 beside the
     currency of a plan or an account, and showed «125403,». */
  .control {
    @include figure-fit(var(--text-body));
  }
}

/* No width of its own (MOL-318), as `NavRow`'s label: a select is as wide as its longest option — a long
   name of one's own category — and that width went into the least width of its sheet or card. */
.control {
  flex: 1;
  width: 0;
  min-width: 0;
  min-height: var(--touch-target);
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: var(--text-body);
  font-variant-numeric: tabular-nums;

  /* The ring is drawn on the well, around the tail as well as the value. */
  outline: none;

  &::placeholder {
    color: var(--text-muted);
    opacity: 1;
  }
}

.select {
  appearance: none;
  padding-right: var(--space-6);
}

.placeholder {
  color: var(--text-muted);
}

/* With large text the chosen option is words that wrap, the select over them unseen and still the one tapped,
   focused and read out (MOL-321): its own line held «Армянский драм · AMD» in 92 px at 200 % and showed «Армя», a
   country «Арм», a currency of «Валюта» nothing at all beside an amount. As the date's drawn words (`.shown`). */
.chosen-words {
  display: none;
}

html[data-large-text] {
  .chosen-words {
    display: block;
    flex: 1;
    min-width: 0;
    padding: var(--space-2) var(--space-6) var(--space-2) 0;
    font-size: var(--text-body);
    overflow-wrap: anywhere;
    pointer-events: none;
  }

  .select {
    position: absolute;
    inset: 0;
    width: 100%;
    opacity: 0;
  }
}

.chevron {
  @include icon;

  font-size: var(--icon);
  position: absolute;
  right: var(--space-3);
  color: var(--text-muted);
  pointer-events: none;
}

textarea.control {
  min-height: calc(var(--touch-target) * 2);
  padding: var(--space-3) 0;
  line-height: var(--leading-body);
  resize: vertical;
}

.shown {
  position: absolute;
  inset: 0 var(--space-3);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text);
  font-size: var(--text-body);
  pointer-events: none;
}

.shown-icon {
  @include icon;

  font-size: var(--icon);
  color: var(--text-muted);
}

/* The native field keeps its place and its taps; only its own rendering of the date is hidden. */
.veiled {
  color: transparent;

  &::-webkit-datetime-edit {
    color: transparent;
  }

  /* Chrome's own calendar would be a second one beside the drawn icon: kept in its place, unseen.
     Stretched over the field it caught taps far outside it — the sheet's «Сохранить» among them.
     On a phone a tap anywhere on the field opens the picker by itself. */
  &::-webkit-calendar-picker-indicator {
    opacity: 0;
  }
}

.well:has(textarea) {
  align-items: stretch;
}

.suffix {
  flex: none;
  color: var(--text-muted);
  font-weight: var(--weight-medium);
}

/* The edge doubled, as a row of the kit draws an error (MOL-211, owner's В-3 «а»): a hairline is lost in
   bad light. Inside the edge, so nothing in the well moves. */
.app-field-invalid .well {
  border-color: var(--bad);
  box-shadow: inset 0 0 0 var(--hairline) var(--bad);
}

/* What of the field the figure and its sign do not get: the well's padding and edges, the gap between them. */
$entry-edges: calc(2 * var(--space-4) + 2 * var(--hairline) + var(--space-2));

/* The amount being typed (MOL-235): the well, the figure and the sign of the spending's sheet as it was. */
.app-field-entry {
  container-type: inline-size;

  .well {
    min-height: 4.5rem;
    padding: 0 var(--space-4);
    border-radius: var(--radius-lg);
  }

  /* The figure's own line, as the copies had it: inherited, the body's leading made the input 58, not 54. It
     shrinks with its sign to the well by their length (MOL-321, adversarial З1, И1): at 150 % «12540» was 180 px
     in a field of 125 and showed «2540» as it was typed, «12» and half a «5» once left — the one figure the sheet
     is for. Measured from the field, less the well's edges and the gap between them (`entryWidth`). */
  .control {
    @include display-type;
    @include figure-fit(var(--text-entry), $less: $entry-edges);

    line-height: normal;
  }

  .suffix {
    @include display-type;
    @include figure-fit(var(--text-entry-sign), $less: $entry-edges);
  }
}

.counter-spoken {
  @include visually-hidden;
}

.counter {
  margin: var(--space-2) 0 0;
  color: var(--text-muted);
  font-size: var(--text-footnote);
  font-variant-numeric: tabular-nums;
  text-align: right;

  &:empty {
    margin: 0;
  }
}

.error {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: var(--space-1) 0 0;
  color: var(--bad-ink);
  font-size: var(--text-footnote);
  overflow-wrap: anywhere;
}

.alert {
  @include icon;

  font-size: var(--icon-sm);
}

.app-field-readonly {
  .well {
    background: var(--surface);
  }

  .control {
    color: var(--text-muted);
  }
}

@media (prefers-reduced-motion: reduce) {
  .well {
    transition: none;
  }
}
</style>
